<!--
	lib/seo/Meta.svelte — plan/08 §2. One typed component every page uses in
	<svelte:head>: <title> (meta.titlePattern(page); home uses meta.homeTitle
	verbatim), meta description (≤155 chars), absolute canonical (PUBLIC_SITE_URL
	+ path, no trailing slash), OG + Twitter summary_large_image, the OG
	image's dimensions/alt, and an `og:video` loop that Messages and other
	chat apps play in the link preview (lib/seo/og-video.ts). `theme-color` is
	NOT repeated here — it's already set once in app.html and doesn't vary per
	page.

	Canonical/OG `path` comes from `page.url.pathname`, not a prop: every route
	has exactly one real path, so deriving it removes a whole class of
	copy-paste mismatch. That's safe here (unlike query/hash — see Pricing.svelte)
	because pathname is fixed per route, not per-visitor, so it's identical
	whether this renders at prerender time or in the browser.

	Public pages (lib/seo/pages.ts) also get a `rel="alternate"` link to their
	Markdown twin, the clean copy AI agents read (lib/seo/llms.ts), and every
	public page but home gets a BreadcrumbList.
-->
<script lang="ts">
	import { page } from '$app/state';
	import { env } from '$env/dynamic/public';
	import { meta, site } from '$lib/content/site';
	import { breadcrumbJsonLd, jsonLdScriptTag } from './jsonld';
	import { markdownPath, publicPage } from './pages';
	import { ogVideo, ogVideoPath, type OgVideoClip } from './og-video';

	let {
		title,
		home = false,
		description = meta.homeDescription,
		image = '/og.png',
		type = 'website',
		video = 'home',
		noindex = false
	}: {
		/** Page label passed through `meta.titlePattern` (e.g. "Pricing", a legal doc's `doc.title`). Ignored when `home` is true. */
		title?: string;
		/** Home uses `meta.homeTitle` verbatim instead of the "{page} — PPF Engine" pattern. */
		home?: boolean;
		/** ≤155 chars. Defaults to the site-wide description for pages that don't need their own. */
		description?: string;
		/** Root-relative or absolute OG/Twitter image. Defaults to the generated design-system OG image (scripts/og-image.mjs). */
		image?: string;
		/** og:type. */
		type?: string;
		/** Link-preview loop (lib/seo/og-video.ts); `null` for none. Noindex pages never get one. */
		video?: OgVideoClip | null;
		/** Adds <meta name="robots" content="noindex,nofollow"> and omits the canonical link (thanks, utility pages). */
		noindex?: boolean;
	} = $props();

	const siteUrl = $derived((env.PUBLIC_SITE_URL || site.url).replace(/\/$/, ''));
	// trailingSlash:'never' (root +layout.ts) already guarantees no trailing
	// slash on any real route except "/" itself, which needs its own case so
	// the canonical for home is exactly `siteUrl`, not `siteUrl/`.
	const canonicalPath = $derived(page.url.pathname === '/' ? '' : page.url.pathname);
	const canonical = $derived(`${siteUrl}${canonicalPath}`);
	const fullTitle = $derived(
		home ? meta.homeTitle : title ? meta.titlePattern(title) : meta.homeTitle
	);
	const ogImage = $derived(image.startsWith('http') ? image : `${siteUrl}${image}`);
	const ogVideoUrl = $derived(video && !noindex ? `${siteUrl}${ogVideoPath(video)}` : null);
	const listed = $derived(noindex ? undefined : publicPage(page.url.pathname));
</script>

<svelte:head>
	<title>{fullTitle}</title>
	<meta name="description" content={description} />
	{#if noindex}
		<meta name="robots" content="noindex,nofollow" />
	{:else}
		<link rel="canonical" href={canonical} />
	{/if}
	{#if listed}
		<link rel="alternate" type="text/markdown" href="{siteUrl}{markdownPath(listed.path)}" />
		{#if listed.path !== '/'}
			<!-- eslint-disable-next-line svelte/no-at-html-tags -- build-time-only JSON-LD from first-party content modules, never user input -->
			{@html jsonLdScriptTag(breadcrumbJsonLd(listed.path, listed.title))}
		{/if}
	{/if}

	<meta property="og:type" content={type} />
	<meta property="og:site_name" content={site.name} />
	<meta property="og:title" content={fullTitle} />
	<meta property="og:description" content={description} />
	<meta property="og:url" content={canonical} />
	<meta property="og:image" content={ogImage} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content={fullTitle} />
	{#if ogVideoUrl}
		<meta property="og:video" content={ogVideoUrl} />
		<meta property="og:video:secure_url" content={ogVideoUrl} />
		<meta property="og:video:type" content={ogVideo.type} />
		<meta property="og:video:width" content={String(ogVideo.width)} />
		<meta property="og:video:height" content={String(ogVideo.height)} />
	{/if}

	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={fullTitle} />
	<meta name="twitter:description" content={description} />
	<meta name="twitter:image" content={ogImage} />
</svelte:head>
